import React, { useCallback, useContext, useMemo, useRef } from "react";

import { TransactionView } from "@common/types/views/transactionViews";
import { isEqual } from "@common/helpers/comparison";
import { omitBy } from "@common/helpers/objects";
import { ViewToolbar } from "@components/views/ViewToolbar/ViewToolbar";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { WORKSPACE_USER_CACHE_KEYS } from "@hooks/useWorkspaceUserCache";
import { useSubNavigationPortalContent } from "@hooks/useSubNavigationPortal";
import {
    PaginatedTransactionsActionsContext,
    PaginatedTransactionsDataContext,
} from "@components/transactions/PaginatedTransactionsTable.context";
import {
    DEFAULT_FILTERS,
    FilterProps,
    FiltersChangeFn,
} from "@components/transactions/filters/lib";
import { FilterTabs } from "@components/pageHeader/filters/lib";
import { TransactionsView } from "@components/transactions/views/TransactionsView";
import { useTransactionViewConfig } from "@components/transactions/views/useTransactionViewConfig";
import { TransactionsViewFooterProps } from "@components/transactions/views/TransactionsViewFooter";
import { useBulkActions } from "@components/transactions/TransactionsBulkActions/useBulkActions";
import { AccrualReviewProvider } from "./AccrualReviewContext";
import { AccrualReviewBulkActionsFooter } from "./AccrualReviewBulkActionsFooter";
import { useBuiltInAccrualReviewViews } from "./BuiltInAccrualReviewViews";
import styles from "./AccrualReviewPage.module.scss";

interface AccrualReviewPageProps {
    accrualEntityIds: Set<number>;
}

export const AccrualReviewPage: React.FC<AccrualReviewPageProps> = ({
    accrualEntityIds,
}) => (
    <AccrualReviewProvider>
        <AccrualReviewPageContent accrualEntityIds={accrualEntityIds} />
    </AccrualReviewProvider>
);

const AccrualReviewPageContent: React.FC<AccrualReviewPageProps> = ({
    accrualEntityIds,
}) => {
    const {
        shownTransactionId,
        filters: currentFilters,
        currentView,
    } = useContext(PaginatedTransactionsDataContext);
    const {
        setShownTransactionId,
        setPage,
        setCurrentFilters,
        setCurrentView,
    } = useContext(PaginatedTransactionsActionsContext);

    const bulkActions = useBulkActions();
    const builtInViews = useBuiltInAccrualReviewViews(accrualEntityIds);
    const transactionsViewConfig = useTransactionViewConfig();

    const viewToolbarContent = useMemo(
        () => (
            <ViewToolbar
                view={currentView}
                onViewChange={setCurrentView}
                viewConfig={transactionsViewConfig}
                builtInViews={builtInViews}
                defaultFilters={DEFAULT_FILTERS}
                baseViewCacheKey={
                    WORKSPACE_USER_CACHE_KEYS.ACCRUAL_REVIEW_PAGE_BASE_VIEW_ID_CACHE_KEY
                }
            />
        ),
        [currentView, setCurrentView, builtInViews, transactionsViewConfig],
    );

    useSubNavigationPortalContent(
        "accrual-review-view-toolbar",
        viewToolbarContent,
    );
    const bulkActionsRef = useRef(bulkActions);
    bulkActionsRef.current = bulkActions;

    const currentFiltersRef = useRef(currentFilters);
    currentFiltersRef.current = currentFilters;

    const handleFiltersChange = useCallback<FiltersChangeFn>(
        (value) => {
            const filters = currentFiltersRef.current;
            const merged = { ...filters, ...value };
            const newFilters = omitBy(
                merged,
                (v) => v === null || v === undefined,
            );

            if (isEqual(newFilters, filters)) {
                return;
            }

            bulkActionsRef.current.clearSelection();
            setPage(1);
            setShownTransactionId(undefined);
            setCurrentFilters(newFilters);
        },
        [setPage, setShownTransactionId, setCurrentFilters],
    );

    const availableFilters = useMemo(
        () => [
            FilterTabs.DATE,
            FilterTabs.CATEGORY,
            FilterTabs.AMOUNT,
            FilterTabs.COUNTERPARTY,
        ],
        [],
    );

    const filterProps: FilterProps = {
        filters: currentFilters,
        onChange: handleFiltersChange,
    };

    const setSelectedTransactionId = useCallback(
        (id: number | string | undefined) => {
            setShownTransactionId(typeof id === "number" ? id : undefined);
        },
        [setShownTransactionId],
    );

    const renderFooter = useCallback(
        (options?: TransactionsViewFooterProps) => (
            <AccrualReviewBulkActionsFooter scrollTo={options?.scrollTo} />
        ),
        [],
    );

    // Ensure bulk actions and selection are always enabled even when the user's
    // cached view doesn't include these options from the default view definition.
    const viewWithBulkActions = useMemo<TransactionView>(
        () => ({
            ...(currentView as TransactionView),
            options: {
                ...(currentView as TransactionView).options,
                bulkActions: true,
                selection: true,
            },
        }),
        [currentView],
    );

    return (
        <section className={styles.accrualReviewPage}>
            <TablePageHeader
                search={filterProps.filters.search}
                onSearchChange={(value) => {
                    filterProps.onChange({ search: value ?? "" });
                }}
                filters={filterProps.filters}
                onFiltersChange={filterProps.onChange}
                defaultFilters={DEFAULT_FILTERS}
                hideClearButton
                availableFilters={availableFilters}
                isFullPage
            >
                <EntityFilterCombobox
                    filters={filterProps.filters}
                    onChange={filterProps.onChange}
                    excludeAllMockEntities
                    onlyBusiness
                />
            </TablePageHeader>

            <section className={styles.body}>
                <TransactionsView
                    view={viewWithBulkActions}
                    setSelectedTransactionId={setSelectedTransactionId}
                    selectedTransactionId={shownTransactionId}
                    fixedTopOffsetPx
                    footer={renderFooter}
                />
            </section>
        </section>
    );
};
